import React, { useRef, useEffect } from 'react';

/**
 * HTML Editor with line numbers, optimized for HTML code display
 */
const HtmlEditorWithLines = React.forwardRef(({
  value,
  onChange,
  placeholder,
  style = {},
  readOnly = false,
}, ref) => {
  const textareaRef = useRef(null);
  const lineNumbersRef = useRef(null);
  
  // Sync ref with the forwarded ref
  useEffect(() => {
    if (ref) {
      if (typeof ref === 'function') ref(textareaRef.current);
      else ref.current = textareaRef.current;
    }
  }, [ref]);
  
  // Handle syncing line numbers with textarea scrolling
  useEffect(() => {
    const handleScroll = () => {
      if (lineNumbersRef.current && textareaRef.current) {
        lineNumbersRef.current.scrollTop = textareaRef.current.scrollTop;
      }
    };
    
    const textarea = textareaRef.current;
    textarea?.addEventListener('scroll', handleScroll);
    return () => textarea?.removeEventListener('scroll', handleScroll);
  }, []);
  
  // Render line numbers
  const renderLineNumbers = () => {
    const lines = (value || '').split('\n');
    return (
      <div 
        ref={lineNumbersRef}
        style={{
          position: 'absolute',
          left: 0,
          top: 0,
          width: '40px',
          height: '100%',
          overflow: 'hidden',
          background: '#f6f8fa',
          borderRight: '1px solid #ddd',
          color: '#888',
          fontSize: '14px',
          fontFamily: 'monospace',
          textAlign: 'right',
          paddingTop: '10px',
          paddingRight: '5px',
          boxSizing: 'border-box',
          userSelect: 'none',
          zIndex: 1
        }}
      >
        {lines.map((_, i) => (
          <div key={i} style={{
            height: '21px',
            lineHeight: '1.5',
          }}>
            {i + 1}
          </div>
        ))}
      </div>
    );
  };
  
  return (
    <div style={{ position: 'relative', width: '100%', height: '100%' }}>
      {renderLineNumbers()}
      <textarea
        ref={textareaRef}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        readOnly={readOnly}
        spellCheck="false"
        autoComplete="off"
        autoCorrect="off"
        autoCapitalize="off"
        style={{
          width: '100%',
          height: '100%',
          minHeight: '300px',
          padding: '10px 10px 10px 45px',
          fontSize: '14px',
          fontFamily: 'monospace',
          lineHeight: '1.5',
          border: 'none',
          outline: 'none',
          boxSizing: 'border-box',
          resize: 'vertical',
          backgroundColor: readOnly ? '#f8f9fa' : 'white',
          cursor: readOnly ? 'default' : 'text',
          ...style
        }}
      />
    </div>
  );
});

export default HtmlEditorWithLines; 